import { z } from "zod";
import { USER_ROLES } from "./enums.js";
/**
 * The permission contract (spec §2.1) — capabilities, the role → capability map,
 * and the single `can()` resolver.
 *
 * §2.1 is blunt about why this file exists: **never write
 * `if (user.role === "ADMIN")` in a route handler or a component.** Role checks
 * scattered through the codebase are exactly why adding the ACCOUNTANT role
 * later becomes a hunt. Handlers say `requireCap("invoice.cancel")`; the client
 * says `useCan("invoice.cancel")`; both resolve through {@link can} reading
 * {@link ROLE_CAPABILITIES} in THIS file, so UI and API cannot disagree.
 *
 * Adding or changing a role is a data change in the map below, never a code
 * change across handlers.
 *
 * Capability checks compose with feature flags — see `flags.ts` and §18.2 for
 * the required ordering (flag first → 404, then capability → 403).
 */
/* -------------------------------------------------------------------------- */
/* 1. The capability union (§2.1)                                              */
/* -------------------------------------------------------------------------- */
/**
 * Every capability in the system, as namespaced `resource.verb` strings.
 *
 * Declared as a `const` tuple with the union derived from it, so a typo in a
 * `requireCap()` / `useCan()` call is a COMPILE ERROR rather than a silent
 * `false` (§2.1). Order mirrors the §2.1 listing, grouped by resource.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THERE IS DELIBERATELY NO `invoice.edit` CAPABILITY — NOT FOR ANY ROLE.
 *
 * An issued GST invoice is not amendable. The only legal correction paths are
 * cancelling it (§4 — a status change that retains the number; invoices are
 * never hard-deleted) or issuing a credit note (§17.3). §2.1: "Not creating the
 * capability is stronger than granting it to nobody" — a capability that does
 * not exist cannot be granted by a future map edit, cannot be typed into a
 * middleware call, and cannot be quietly enabled to unblock a support request.
 *
 * If you are here because some screen "needs to fix an invoice": it does not.
 * Cancel and re-issue, or raise a credit note. Do not add `invoice.edit`.
 *
 * `invoice.edit_draft` is a DIFFERENT thing and does exist: a DRAFT invoice has
 * not been issued, carries no legal force, and is freely editable until it is.
 * ────────────────────────────────────────────────────────────────────────────
 */
export const CAPABILITIES = [
    // Quotations (§7)
    "quotation.view",
    "quotation.create",
    "quotation.edit",
    "quotation.convert",
    /**
     * §2.1, amended 2026-08-13 — RENAMED FROM `quotation.delete`.
     *
     * The action changed, so the name had to. A quotation is no longer removed
     * from the database: it is moved to the `VOID` status (§4), keeping its number
     * in the continuous `NNN/YY` series (§6) so a customer ringing about "542/26"
     * is told "that one was cancelled" rather than "we have no such quotation",
     * and so §17.4's CA export can still report the serial range it was issued in.
     *
     * §2.1 states the reason for renaming rather than leaving the old string in
     * place: "A capability named for an operation the system does not perform is
     * the kind of drift that costs an afternoon two years later — the name is the
     * first thing a reader trusts." There is deliberately no `quotation.delete`
     * alias kept for compatibility; the union is the contract both the API and the
     * SPA compile against, so the rename is a compile error everywhere at once,
     * which is the whole point of declaring it here.
     */
    "quotation.void",
    // Invoices (§7). Note: no `invoice.edit` — see the block comment above.
    "invoice.view",
    "invoice.create",
    "invoice.edit_draft",
    "invoice.cancel",
    /**
     * Attach the registration an outside portal returned: IRN, acknowledgement
     * number, acknowledgement date and the IRP's signed QR payload (§10).
     *
     * ─────────────────────────────────────────────────────────────────────────
     * THE ONE WRITE THE ACCOUNTANT HOLDS, AND WHY IT DOES NOT BREAK §2.
     *
     * §2 says the CA "cannot create, edit, cancel or delete anything", and that
     * rule is about the CONTENT of a document — its number, its lines, its
     * customer, its totals. This capability touches none of them. It records an
     * external fact that the government's Invoice Registration Portal produced
     * about an invoice that already exists, into four columns (§10) that are
     * otherwise unused and that no figure on the document is derived from.
     *
     * Recording it is not amending the invoice; it is filing the receipt for it.
     * The alternative — the CA mailing an IRN to the son to type in — leaves
     * every large bill sitting unregistered in the app until someone gets to it,
     * and puts a transcription step between the portal and a legal identifier.
     *
     * IT IS DELIBERATELY NOT PART OF `settings.write` OR `invoice.create`. A CA
     * who could create invoices, or reach the bank details, is a different and
     * much larger grant. This is the narrowest capability that makes the workflow
     * work, and `permissions.test.ts` names it as the SINGLE allowed exception to
     * the read-only scan — so a second write cannot be slipped in beside it.
     * ─────────────────────────────────────────────────────────────────────────
     */
    "invoice.record_irn",
    /**
     * §6.1 — set `next_invoice_seq` / `next_quote_seq` / `current_fy` directly.
     *
     * Deliberately NOT folded into `settings.write`: that capability also governs
     * the GSTIN, the PAN and the bank details, and §6.1 says in as many words that
     * "the owner has no business editing those". He *does* need the counters,
     * because he keeps writing bills by hand and the series has to be nudged back
     * into line afterwards.
     *
     * Holding it is not permission to move a counter in any direction — see
     * {@link canSetCounter}.
     */
    "invoice.set_counter",
    /**
     * ═════════════════════════════════════════════════════════════════════════
     * RECORD THE 12-DIGIT E-WAY BILL NUMBER THE GOVERNMENT PORTAL ISSUED
     * (Rule 138 CGST) — on an INVOICE, and on a delivery challan for the goods
     * that move without one.
     *
     * THE RESOURCE IS `eway`, NOT `invoice`, AND THAT IS NOT A STYLE CHOICE. One
     * capability guards two documents: the e-way bill rides against the TAX
     * INVOICE (confirmed with the owner's CA, 2026-08-17, works-contract
     * dispatches included), and Rule 55 movements that never produce an invoice
     * carry their own. A capability named `invoice.record_ewb` guarding a challan
     * route would be a name that does not match the action — precisely the drift
     * `quotation.void` was renamed to prevent. The artifact is the e-way bill; the
     * document it is attached to is an implementation detail of the movement.
     *
     * IT IS DELIBERATELY NOT `invoice.create`, AND THE CA IS THE REASON. She is
     * the one who logs into the government portal — the owner does not — so she is
     * the person most likely to have the number in front of her. Gating it behind
     * the authoring capability would mean granting the CA the power to raise
     * invoices in order to let her type twelve digits she did not choose. Recording
     * a fact the government has already issued is not privileged authorship: it
     * changes no line, no total, no party and no number on the document, exactly
     * like `invoice.record_irn` above.
     *
     * THE OWNER AND THE SON HOLD IT TOO. The point is that whoever has the sheet
     * in front of them can record it; routing it through one person is the
     * bottleneck this capability exists to remove.
     *
     * THERE IS NO `eway.generate`, AND THERE NEVER WILL BE. Direct e-way bill API
     * access needs turnover above ₹10 crore, or a paid GSP below that; this
     * business is far below either. SteelBill records what the portal issued and
     * claims nothing.
     *
     * THERE IS NO `eway.override_requirement` EITHER, and its absence is a design
     * decision rather than an omission. The "this bill needs an e-way bill" state
     * is not a stored flag that nags until someone clears it — it is a
     * NOTIFICATION, and marking it read IS the acknowledgement. So there is
     * nothing to override, and no reason for anyone to clear a false alarm by
     * typing a fake number, which is the workaround an un-clearable nag actually
     * produces.
     * ═════════════════════════════════════════════════════════════════════════
     */
    "eway.record",
    // Delivery challans (§17.7, behind the `challans` flag)
    "challan.view",
    "challan.create",
    "challan.edit",
    "challan.cancel",
    /**
     * Credit notes (§17.3, section 34(1) CGST, behind the `credit_notes` flag).
     *
     * ─────────────────────────────────────────────────────────────────────────
     * THERE IS NO `creditnote.edit`, FOR THE SAME REASON THERE IS NO
     * `invoice.edit` — see the block comment at the top of this file.
     *
     * A credit note is a tax document. Once issued it has gone to the customer,
     * it reduces output tax liability, and it is declared in the CDNR table of
     * that month's GSTR-1. Amending one after the fact is exactly the operation
     * §17.3 exists to make unnecessary: the correction path for a credit note
     * that was wrong is to CANCEL it (the number is retained, the credited amount
     * returns to the invoice's outstanding balance) and issue another.
     *
     * This capability does not exist rather than existing and being granted to
     * nobody, because a capability that is absent from the union cannot be typed
     * into a `requireCap()` call, cannot be added to a role by a one-line map
     * edit, and cannot be quietly enabled to unblock a support request.
     *
     * There is no `creditnote.edit_draft` either, and no draft: unlike an invoice,
     * a credit note is a reason, an original and usually one line, typed and
     * issued in one sitting. `CreditNoteStatus` in `schema.prisma` says the same
     * thing on the database side and explains it at more length.
     *
     * DEBIT NOTES (§34(3)) ARE OUT OF SCOPE and get no capability here — not even
     * an unused one. When they are built they are a separate document with a
     * separate table, series and `debitnote.*` capabilities.
     * ─────────────────────────────────────────────────────────────────────────
     */
    "creditnote.view",
    "creditnote.create",
    "creditnote.cancel",
    /**
     * Payments (§17.5, behind the `payments` flag).
     *
     * ─────────────────────────────────────────────────────────────────────────
     * THE RESOURCE IS `payment`, NOT `invoice`, AND THAT IS THE WHOLE POINT.
     *
     * Recording money received is not an operation on the tax invoice. A payment
     * is a row in a ledger (see `Payment` in schema.prisma) — the invoice is not
     * touched, its status does not move, and there is deliberately no `PAID`
     * value in `InvoiceStatus` for one to move to. Naming these `invoice.*` would
     * put the receipt inside the document's permission surface, so that widening
     * "who may record a receipt" would silently widen "who may act on an invoice"
     * — exactly the coupling §2.1 asks to be kept out of the map.
     *
     * The practical consequence is the CA: `payment.view` sits in the shared view
     * row, so she can reconcile the ledger against the bank, while every invoice
     * write stays denied to her.
     *
     * There is NO `payment.edit`. A payment that was typed wrong is deleted and
     * re-entered — an amount, a date and a reference are the whole row, so
     * "editing" one is indistinguishable from replacing it, and two paths to the
     * same change is two places for the audit trail to be incomplete.
     * ─────────────────────────────────────────────────────────────────────────
     */
    "payment.view",
    "payment.record",
    /**
     * Remove a payment row that records something that never happened.
     *
     * IT IS A REAL DELETE, unlike `quotation.void` — and the difference is not
     * inconsistency. `quotation.delete` was renamed because the system stopped
     * deleting quotations: they hold a number in a continuous series (§6) and are
     * evidence of what was quoted. A payment row holds no number, prints on no
     * document, and a mistyped one is a record of money that never moved. The
     * name matches the operation, which is all §2.1 asked of the rename.
     *
     * IT IS NOT A REFUND. Refunds are not modelled (§17.5), and a negative
     * payment must never be invented to represent one — every outstanding figure
     * in the system is a `SUM` over this table.
     */
    "payment.delete",
    /**
     * ═════════════════════════════════════════════════════════════════════════
     * MY OWN NOTIFICATIONS (§17.7's e-way bill loop, behind the `notifications`
     * flag) — read the list, and mark one read.
     *
     * TWO CAPABILITIES, BOTH HELD BY EVERY ROLE, AND BOTH ABOUT ONE PERSON'S OWN
     * ROWS. A notification is TARGETED AT A PERSON and carries its own read state,
     * which is the whole reason it is a table rather than a derived list: "the CA
     * has read it, the owner has not" is a fact that has to be stored. So these
     * are not permissions over anybody else's data — the routes filter on the
     * caller's own user id, and a notification addressed to someone else answers
     * 404, never 403, because its existence is not the caller's business.
     *
     * `notification.mark_read` IS A WRITE AND IS STILL GRANTED TO THE CA. §2 keeps
     * her read-only over DOCUMENT CONTENT — nothing here touches a document. A
     * read receipt on her own row changes no figure, no line and no party; the
     * alternative is an inbox she can never clear, which is an inbox she stops
     * opening, which defeats the one feature she is the flagship recipient of.
     * `permissions.test.ts` names it as an explicit exception rather than letting
     * a pattern match wave it through.
     *
     * THERE IS NO `notification.send` AND NO `notification.delete`. Notifications
     * are raised by the SYSTEM from events the app already records — no human
     * composes one, so there is no authorship to grant. (An in-app MESSENGER, a
     * shared conversation with different read semantics, is a later build with its
     * own table and its own capabilities; leave room for it, grant nothing here.)
     * Nothing deletes one either: retention is a housekeeping question for the
     * ADMIN screen, not a permission a person exercises row by row.
     * ═════════════════════════════════════════════════════════════════════════
     */
    "notification.view",
    "notification.mark_read",
    /**
     * ═════════════════════════════════════════════════════════════════════════
     * THE IN-APP MESSENGER (behind the `messages` flag) — read the conversation,
     * and add to it.
     *
     * TWO CAPABILITIES, BOTH HELD BY EVERY ROLE, AND THEY ARE NOT THE
     * NOTIFICATION PAIR ABOVE WEARING DIFFERENT NAMES. A notification is raised
     * BY THE SYSTEM and addressed TO A PERSON; there is no `notification.send`
     * because nobody composes one. A message is the opposite on both counts —
     * composed by a human, addressed to the room — so authorship is a real grant
     * that a real person exercises, and it gets its own name.
     *
     * `message.send` IS A WRITE AND IS STILL GRANTED TO THE CA — the fourth
     * write she holds, and the first that is not "file something the government
     * issued". §2 keeps her read-only over DOCUMENT CONTENT: a message changes
     * no line, no figure, no party and no number on any document. It is a
     * sentence in a conversation, and a CA who can read the conversation but not
     * answer "that HSN is wrong on 26-27/67" is worse than useless — the owner
     * would get the answer by phone and nothing would be written down.
     * `permissions.test.ts` names it as an explicit exception rather than
     * letting a pattern wave it through.
     *
     * THERE IS NO `message.delete` AND NO `message.edit`, AND THERE NEVER WILL
     * BE — the same construction as `invoice.edit` (see the top of this file):
     * not creating the capability is stronger than granting it to nobody. This
     * is a business record between three people about tax documents. A message
     * that can be edited after the fact is a record of what somebody says they
     * said; a message that can be deleted is one that vanishes precisely when a
     * dispute makes it matter. An awkward sentence left standing is the cheaper
     * of the two failures, every time.
     *
     * THERE IS NO `message.mark_read` EITHER, and its absence is a decision.
     * Reading the conversation is what advances a reader's own watermark, so
     * `message.view` is the capability that authorises it: a separate grant
     * could only ever be held by everyone who holds `message.view` (a reader who
     * may not record that he has read is a reader with a badge he can never
     * clear), which makes it a capability that can never be denied — and a
     * capability that cannot be denied is decoration.
     * ═════════════════════════════════════════════════════════════════════════
     */
    "message.view",
    "message.send",
    /**
     * ═════════════════════════════════════════════════════════════════════════
     * DIRECT A MESSAGE AT ONE PERSON RATHER THAN AT THE ROOM (2026-08-18, behind
     * the same `messages` flag) — the private half of the messenger.
     *
     * READ THE NAME AS A VERB PHRASE: "direct a message". The resource is still
     * `message`; what is granted is ADDRESSING one, which is the only thing that
     * differs between the two halves of this feature.
     *
     * ONE CAPABILITY, NOT A `view`/`send` PAIR LIKE THE ROOM ABOVE, AND THE
     * ASYMMETRY IS THE ARGUMENT. The room splits because a read-only observer is
     * a person somebody could plausibly want: read the conversation, add nothing
     * to it. A private conversation has no third party to observe it — you only
     * ever read your OWN — so a `direct.view` without a `direct.send` describes a
     * person who receives messages he is forbidden to answer. That is the badge
     * nobody can clear, one paragraph up, wearing different clothes. One door.
     *
     * IT IS NOT DECORATION, WHICH IS THE TEST `message.mark_read` FAILED. That one
     * could only ever be held by everybody who holds `message.view`. This one is
     * genuinely deniable, and the case is concrete: a business may want an outside
     * professional confined to the room, where everything said is on the record in
     * front of everyone, and no side channel exists. Withhold it and that person
     * keeps the shared conversation and can neither open a private one nor be
     * dragged into one — because SENDING checks the capability at BOTH ends (see
     * `services/direct-messages.ts`). A private conversation takes two people who
     * are each allowed to hold one.
     *
     * WHAT IT DOES NOT AND MUST NOT GRANT: reading somebody else's conversation.
     * No holder of this capability, at any level, can read a message between two
     * other people — that is not a permission the map can express and it is not
     * one the endpoints offer. Every direct route resolves the caller's own id as
     * one half of the pair, from the session. A third party asking for a
     * conversation gets a 404, because whether it exists is not their business.
     *
     * THERE IS STILL NO `message.delete` AND NO `message.edit`, and a private
     * message is no more erasable than a public one. "Private" here means "not
     * shown to the third person", never "off the record": the send is audited like
     * every other mutation (§13). What the audit row deliberately does NOT carry
     * is the BODY — see `services/direct-messages.ts`, where withholding it is the
     * difference between a private message and one the `audit.view` holder can
     * read at leisure.
     * ═════════════════════════════════════════════════════════════════════════
     */
    "message.direct",
    /**
     * ═════════════════════════════════════════════════════════════════════════
     * SEND A DOCUMENT OUT OF THE BUSINESS BY EMAIL (§17A.2 stage 1, behind the
     * `mail` flag) — the quotation today, the invoice next.
     *
     * THE RESOURCE IS `mail`, NOT `quotation`, AND THAT IS THE WHOLE ARGUMENT.
     * Three names were possible and two of them are wrong:
     *
     *  • `quotation.send` would have to be joined by `invoice.send` in the very
     *    next commit — §17A.2 stage 1 is "Invoice or quotation goes out with its
     *    PDF attached" — and then by `report.send` when §17.4's monthly register
     *    is mailed to the CA. Three capabilities for one act, each guarding a
     *    different door onto the same SMTP session, and the day someone must be
     *    stopped from mailing anything at all it is three map edits and a hunt.
     *  • REUSING `quotation.edit` or `quotation.create` would say that authoring
     *    an offer and putting it in a customer's inbox are one grant. They are
     *    not: the first produces a row this business can throw away, the second
     *    speaks in the business's name, from its own domain address, to a
     *    pharmaceutical company's accounts-payable system, and cannot be recalled.
     *    §2.1's test for a capability is whether the two acts could ever need to
     *    be granted apart, and these plainly can — a future clerk who prepares
     *    quotations for the owner to send is the obvious case.
     *  • `quotation.view` is not even close. Sending is a WRITE: it produces a
     *    `mail_messages` row, moves the document's status, and is audited.
     *
     * So the artifact is the MESSAGE, and the document it carries is a detail of
     * the message — exactly the reasoning that made `eway.record` an `eway`
     * capability rather than an `invoice` one.
     *
     * WHO HOLDS IT: OWNER and ADMIN ({@link SEND_MAIL}). NOT the ACCOUNTANT, and
     * this one is not a close call. §2 keeps her read-only over document content,
     * and the four writes she does hold are all "file something that already
     * happened" — an IRN, an e-way bill number, her own read receipt, a sentence
     * in an internal conversation. Mailing a quotation to a customer is none of
     * those: it is outbound correspondence in the proprietor's name, and the
     * customer cannot tell which of the three people pressed the button.
     *
     * THERE IS NO `mail.send_to` OR ANY WAY TO NAME THE RECIPIENT. The address is
     * the CUSTOMER's, read off the customer row by the server; nothing in the API
     * accepts an arbitrary address, so this capability cannot be turned into an
     * open relay by a client that sends one more field.
     * ═════════════════════════════════════════════════════════════════════════
     */
    "mail.send",
    /**
     * ═════════════════════════════════════════════════════════════════════════
     * OPEN THE MAILBOX — READ WHAT THIS BUSINESS HAS SENT AND RECEIVED (§17A.1).
     *
     * A SEPARATE CAPABILITY FROM `mail.send`, NOT A PREREQUISITE OF IT, and the
     * split is the one §2.1 asks for: could the two ever need to be granted
     * apart? Plainly yes, in both directions. A future clerk who mails quotations
     * the owner has approved needs `mail.send` and has no business reading the
     * correspondence file; the CA, if she is ever given the register of what went
     * to whom, needs exactly the read and must never get the send. Making the
     * read a free consequence of holding the send would make the first of those
     * impossible to express.
     *
     * IT IS NOT `message.view`, EITHER. That is the INTERNAL room — three people
     * with logins, talking to each other. This is the OUTSIDE record: customers'
     * addresses, what left the building, and (once anything syncs inbound) what
     * they wrote back. Different table, different counterparty, different reason
     * to be read. One capability over both would mean widening "who may read the
     * internal chat" silently widened "who may read every customer's email".
     *
     * WHO HOLDS IT: OWNER and ADMIN ({@link READ_MAIL}). The ACCOUNTANT does not,
     * and the reason is the same §2 reason she does not hold `mail.send`, minus
     * the outbound half: this is the proprietor's correspondence with his
     * customers, not the content of the tax documents she files. Nothing she
     * needs to reconcile or return lives here — an invoice she must see is in the
     * invoice register, gated on `invoice.view`, which she holds. If a case is
     * ever made for her, it is one line in the role map and no route changes,
     * which is the whole point of it being its own capability.
     *
     * THERE IS NO `mail.delete`, NO `mail.edit` AND NO `mail.mark_read`. The
     * first two are the `invoice.edit` construction again: a sent-mail row is the
     * answer to "did we send it, when, and to which address", asked months later
     * by somebody disputing it, and a record that can be edited or deleted is
     * worth nothing at the moment it matters. The third is the reasoning
     * `message.mark_read` records one block above — reading is what advances a
     * read state, so a separate grant could only be held by everyone holding
     * `mail.view`, and a capability that can never be denied is decoration.
     * ═════════════════════════════════════════════════════════════════════════
     */
    "mail.view",
    /**
     * ═════════════════════════════════════════════════════════════════════════
     * PUT A QUOTATION IN THE CUSTOMER'S HANDS, AND RECORD THAT IT WENT (§17.1,
     * behind the `approval_links` flag) — issue the signed approval link, and
     * stamp `sent_at`.
     *
     * THE RESOURCE IS `quotation` AND THE VERB IS `share`, WHICH IS NEITHER OF
     * THE TWO NAMES THAT SUGGEST THEMSELVES:
     *
     *  • IT IS NOT `mail.send`. That capability's own note says the artifact is
     *    the MESSAGE — an SMTP session, a mailbox, an address the server chose.
     *    Two of the three doors §17.1 opens involve no message at all: a QR
     *    printed on a sheet handed across a counter, and "record sent" for a job
     *    discussed on the telephone. Gating those on the mail capability would
     *    mean a business with no mailbox configured cannot record that it handed
     *    a quotation over, and would tie a paper workflow to `SMTP_PASSWORD`.
     *
     *  • IT IS NOT `quotation.edit` OR `quotation.create`. Authoring an offer
     *    produces a row this business can still throw away; issuing a link mints
     *    a BEARER CREDENTIAL that a stranger can use to approve the document
     *    without signing in, and stamping `sent_at` asserts a fact about the
     *    outside world. §2.1's test — could the two ever need to be granted
     *    apart? — answers itself: the future clerk who prepares quotations for
     *    the owner to send is exactly the person who gets one and not the other.
     *
     * WHO HOLDS IT: OWNER and ADMIN ({@link SHARE_DOCUMENTS}). NOT the
     * ACCOUNTANT, for the reason she does not hold `mail.send` — this speaks to a
     * customer in the proprietor's name, and the customer cannot tell which of
     * the three people pressed the button.
     *
     * WHAT IT DOES NOT GRANT, AND MUST NEVER: deciding. There is deliberately no
     * `quotation.approve` and no `quotation.reject` capability for anybody to
     * hold on this surface. The customer's answer arrives through the public
     * portal, carried by a signed token that names ONE quotation, and nothing an
     * employee holds can forge it — a staff member who marks a quotation approved
     * does it through `PATCH /quotations/:id/status` under `quotation.edit`,
     * which writes no `decided_at`, no name and no IP. "The customer said yes"
     * and "we recorded that the customer said yes" stay two different rows.
     * ═════════════════════════════════════════════════════════════════════════
     */
    "quotation.share",
    // Customers (§4)
    "customer.view",
    "customer.create",
    "customer.edit",
    // Items / rate card (§4)
    "item.view",
    "item.create",
    "item.edit",
    "item.adjust_stock",
    // Reports & exports (§17.4)
    "report.view",
    "report.export",
    // Settings & master data (§8)
    "settings.view",
    "settings.write",
    "taxcode.write",
    // Administration (§2, §13)
    "user.manage",
    "audit.view",
    /**
     * ═════════════════════════════════════════════════════════════════════════
     * MY OWN ACCOUNT (2026-08-18) — my name, my sign-in identifier, my secret.
     *
     * IT IS NOT `user.manage`, AND THAT IS THE ENTIRE REASON IT EXISTS. Every
     * route on `/api/v1/users` is gated on `user.manage`, which is ADMIN-only, so
     * until this capability landed THE OWNER COULD NOT CHANGE HIS OWN PIN — his
     * son had to do it for him, from a different account, on a different
     * credential. That is the same shape as §6.1's counter override being
     * unreachable by the one man it was built for, and §2 already recorded the
     * two consequences of that shape: he works around it, and the permission
     * model ends up describing the software's convenience rather than the
     * business. A proprietor who must ask his son to rotate his own secret will
     * simply not rotate it.
     *
     * Administering OTHER PEOPLE and maintaining YOUR OWN ACCOUNT are different
     * grants over different data, and §2.1's test — could they ever need to be
     * held apart? — answers itself: they are held apart TODAY, in the shipped
     * map. All three roles hold this one; exactly one holds `user.manage`.
     *
     * ─────────────────────────────────────────────────────────────────────────
     * ONE CAPABILITY, NOT THREE, AND THE SPLIT WAS CONSIDERED AND REJECTED.
     *
     * The obvious alternative is `profile.view` + `profile.edit` +
     * `profile.change_credential`. Ask §2.1's question of each pair and the
     * answer comes back the same way every time: a person who may read their own
     * name but not correct it, or who may correct their name but never rotate a
     * password that has just been shoulder-surfed, is not a person anybody will
     * ever configure. Those capabilities could only ever be granted together, and
     * a capability that can never be denied is decoration — which is the reason
     * this file already gives for there being no `message.mark_read` and no
     * `mail.mark_read`. Splitting for symmetry would produce three names for one
     * fact and three places for a route to name the wrong one.
     *
     * What makes ONE name safe is that the boundary is not "which verb" but
     * WHOSE ROW: every route behind it resolves the target from `req.user.id` and
     * accepts no id from the caller, so there is no request this capability can
     * be pointed at somebody else's account. That is the same construction
     * `notification.view` uses, and it is why the resource is `profile` — the
     * caller's own account — rather than `user`.
     * ─────────────────────────────────────────────────────────────────────────
     *
     * WHAT IT DELIBERATELY DOES NOT COVER, AND WHY NO CAPABILITY SHOULD:
     * changing your own ROLE or your own ACTIVE STATE. Those are not withheld by
     * a permission — they are UNREACHABLE. `ProfileUpdateSchema` is `.strict()`
     * and has no such field, and the service names the columns it writes rather
     * than spreading a body, so there is no value of any capability, held by
     * anybody, that turns this surface into a privilege escalation. Not creating
     * the capability is stronger than granting it to nobody (§2.1) — the same
     * construction as `invoice.edit`, applied to the one form on which "grant
     * myself ADMIN" would otherwise be a text field.
     *
     * NOR DOES IT OPEN A SIGN-IN DOOR. A person may set a secret for a door that
     * is already open on their account; opening one is `user.manage`'s
     * `POST /users/:id/doors`. That separation is what keeps "the owner decided
     * the CA may use a PIN" and "she gave herself one" two different events —
     * see apps/api/src/auth/credential-paths.ts.
     * ═════════════════════════════════════════════════════════════════════════
     */
    "profile.manage",
];
/** Runtime validation for a capability arriving as untrusted data. */
export const CapabilitySchema = z.enum(CAPABILITIES);
/** Narrows an arbitrary runtime value to a {@link Capability}. */
export function isCapability(value) {
    return typeof value === "string" && CAPABILITIES.includes(value);
}
/* -------------------------------------------------------------------------- */
/* 2. The role → capability map (§2.1) — ONE file, this one                    */
/* -------------------------------------------------------------------------- */
/**
 * Capabilities every authenticated role holds: the `*.view` row of the §2.1
 * table, plus `report.view` and `report.export`.
 *
 * PRECEDENCE NOTE: `audit.view` is textually a `*.view` capability but the §2.1
 * table lists it explicitly on the ADMIN-only row, and the more specific row
 * wins — the audit trail is an administration surface, not a document view. It
 * is therefore NOT in this shared base. `settings.view` *is* shared: only
 * `settings.write` is called out as ADMIN-only.
 */
const VIEW_AND_EXPORT = [
    "quotation.view",
    "invoice.view",
    "challan.view",
    /*
     * §17.3. The CA holds this and needs it more than anyone: credit notes are
     * declared in GSTR-1's CDNR table and reduce the output tax she files. A
     * correction she cannot see is a return she files wrong.
     */
    "creditnote.view",
    /*
     * §17.5. The CA holds this, and should: reconciling recorded receipts against
     * the bank statement is her job, and seeing what has been paid changes nothing
     * about what any invoice SAYS. Recording and removing payments are separate
     * capabilities she does not hold — see {@link RECORD_RECEIPTS}.
     */
    "payment.view",
    "customer.view",
    "item.view",
    "settings.view",
    "report.view",
    "report.export",
];
/**
 * The document-authoring capabilities shared by OWNER and ADMIN: `quotation.*`
 * except `void`, `invoice.create` / `edit_draft` (never `invoice.edit` — it does
 * not exist), `challan.*` except `cancel`, `customer.*`, `item.create` and
 * `item.edit`.
 *
 * The four withdrawal verbs are held by the same two roles but live in
 * {@link UNDO_OWN_MISTAKES} rather than here, because they are not authoring: see
 * that constant for why the distinction is worth a second list.
 */
const AUTHOR_DOCUMENTS = [
    "quotation.create",
    "quotation.edit",
    "quotation.convert",
    "invoice.create",
    "invoice.edit_draft",
    /*
     * The OWNER and the son hold this too, not just the CA. The point of the
     * capability is that whoever has the IRN in front of them can record it —
     * routing it through one person is exactly the bottleneck it exists to
     * remove, and the son is the one most likely to be at a keyboard.
     */
    "invoice.record_irn",
    "challan.create",
    "challan.edit",
    /*
     * §17.3 — CREATE ONLY, because there is no `creditnote.edit` to hold. Raising
     * a credit note is authoring a document, so it belongs here; the CA is
     * excluded for the ordinary §2 reason, not a special one.
     */
    "creditnote.create",
    "customer.create",
    "customer.edit",
    "item.create",
    "item.edit",
];
/**
 * §6.1's own row of the §2.1 table: OWNER ✓, ADMIN ✓, ACCOUNTANT —.
 *
 * Its own constant rather than an extra entry in {@link AUTHOR_DOCUMENTS},
 * because setting a counter is not authoring a document: it moves the position
 * of a legally-sequential series without producing anything, and the DIRECTION
 * rule below applies to it and to nothing else.
 */
const COUNTER_MANAGEMENT = ["invoice.set_counter"];
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MONEY IN, AND TAKING BACK AN ENTRY THAT WAS WRONG — OWNER ✓, ADMIN ✓ (§17.5).
 *
 * ITS OWN LIST, not folded into {@link AUTHOR_DOCUMENTS}, and the reason is the
 * same shape as {@link COUNTER_MANAGEMENT}'s: recording a receipt produces no
 * document. It writes a row in a ledger that no customer ever sees, against an
 * invoice that is not touched. Bundling it with authoring would mean that the
 * day someone is trusted to raise a quotation they are also trusted to say a
 * bill has been settled, which is a different decision about a different person.
 *
 * THIS IS THE ROW MOST LIKELY TO MOVE NEXT, which is the practical argument for
 * keeping it separate. `payment.record` is the obvious candidate for a future
 * ACCOUNTANT grant — she is the one with the bank statement in front of her, and
 * unlike every other write it changes no figure on a legal document. It is NOT
 * granted today, because §2 keeps the CA read-only over everything but
 * `invoice.record_irn` and `permissions.test.ts` enforces exactly that one
 * exception; widening it is a spec amendment plus a test change, deliberately,
 * not a quiet addition here. When that argument is made, it is a one-line data
 * change at the map below rather than a hunt through routes.
 *
 * DELETING A PAYMENT IS HERE RATHER THAN IN {@link UNDO_OWN_MISTAKES}, even
 * though it is plainly an undo. That list is about WITHDRAWING A DOCUMENT — a
 * number already in a customer's hands, retained, with a mandatory reason. A
 * deleted payment retracts nothing anyone was ever shown; it corrects a typo in
 * the books. Keeping the two apart means a future role can be given the ledger
 * without being given the power to void a bill, and vice versa.
 *
 * WHAT MAKES THE DELETE SAFE: it is audited with a full before-image
 * (`payment.delete`, §2.1 — denied attempts too), it cannot touch the invoice,
 * and it is not a refund. Refunds are not modelled (§17.5).
 * ═══════════════════════════════════════════════════════════════════════════
 */
const RECORD_RECEIPTS = [
    "payment.record",
    "payment.delete",
];
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FILING WHAT THE GOVERNMENT PORTAL ISSUED — OWNER ✓, ADMIN ✓, **ACCOUNTANT ✓**.
 *
 * ITS OWN LIST BECAUSE ITS ROW IS DIFFERENT FROM EVERY OTHER ONE. It is the only
 * grant in this file that all THREE roles hold, and it is the second write the
 * CA has (after `invoice.record_irn`). Folding it into {@link AUTHOR_DOCUMENTS}
 * would silently hand her the whole authoring set the day someone added her
 * there; folding it into {@link VIEW_AND_EXPORT} would make a write look like a
 * read. A one-line list that appears in three role rows says exactly what is
 * true and nothing more.
 *
 * WHY THE CA HOLDS IT AT ALL: she is the one who logs into the e-way bill portal
 * — the owner does not — so she is the person with the twelve digits in front of
 * her. See the capability's own note above for why recording them is not
 * amending the document.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const RECORD_EWAY_BILLS = ["eway.record"];
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * A PERSON'S OWN NOTIFICATIONS — every role, because every role has an inbox.
 *
 * Separate from {@link VIEW_AND_EXPORT} even though all three roles hold both,
 * because that constant is the §2.1 table's `*.view` ROW — a transcription of
 * the spec — and this pair is not in that table. Keeping them apart means the
 * shared read row still reads as the spec's, and means marking a notification
 * read (a write) is never mistaken for part of it.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const OWN_NOTIFICATIONS = [
    "notification.view",
    "notification.mark_read",
];
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE SHARED CONVERSATION — every role, because a conversation with two of the
 * three people in it is a phone call with a transcript missing.
 *
 * ITS OWN LIST RATHER THAN AN ADDITION TO {@link OWN_NOTIFICATIONS}, even though
 * the same three roles hold both. That constant is about A PERSON'S OWN ROWS —
 * targeted, per-person read state, nobody else's business. This one is about
 * SHARED CONTENT: everybody sees the same rows, and `message.send` puts new
 * content in front of the other two. They are different kinds of grant over
 * different kinds of data, and the day one of them moves (a future read-only
 * observer would plainly get `message.view` and not `message.send`) it should be
 * a one-line change here rather than a split of a merged list.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const CONVERSATION = [
    "message.view",
    "message.send",
];
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE PRIVATE HALF OF THE MESSENGER — every role today, and its own list
 * precisely so that sentence can stop being true in one line.
 *
 * NOT FOLDED INTO {@link CONVERSATION}, even though the same three roles hold
 * both and both are gated by the same flag. The room is TRANSPARENT: whatever
 * the CA writes there, the father and the son both see, which is most of why
 * granting an outside professional a write was defensible at all. A direct
 * message is the opposite property — two people, and the third cannot see it.
 * Those are different decisions about the same person, and the day the owner
 * decides his CA belongs on the record and nowhere else, it is one line removed
 * from the ACCOUNTANT row rather than a split of a merged list.
 *
 * ALL THREE HOLD IT TODAY, and the reason is the reason the room grants her a
 * write. A private channel the CA can be sent but cannot answer is worse than
 * no private channel: the owner asks "did the department notice arrive?" and the
 * reply comes back on WhatsApp, or in the room where his son reads it. Grant it
 * to both ends or to neither — a one-way private conversation is not one.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const PRIVATE_CONVERSATION = ["message.direct"];
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * A PERSON'S OWN ACCOUNT — every role, because every role has one (2026-08-18).
 *
 * ITS OWN LIST RATHER THAN A LINE IN {@link OWN_NOTIFICATIONS}, even though the
 * same three roles hold both and both are "my own rows". That constant is an
 * INBOX: rows the system addressed to this person, which they may read and mark
 * read. This is the ACCOUNT ITSELF — the name on the audit trail, the identifier
 * they sign in with, the secret behind it. Merging them would mean that the day
 * somebody is given a read-only view of their notifications they are also given
 * the power to change the email address their session is bound to.
 *
 * IT IS THE ONE GRANT NO ROLE CAN SENSIBLY BE DENIED, and unlike
 * `message.mark_read` (which this file refused to invent for exactly that
 * reason) it earns a name anyway, because the alternative is not "no capability"
 * — it is `user.manage`, which is ADMIN-only and which is why the OWNER could
 * not change his own PIN until this shipped. A route must carry a capability
 * (§2.1, and `route-capability-coverage.test.ts` enforces it); the only question
 * was WHICH, and the honest answer is not the one that also grants power over
 * three other people's accounts.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const OWN_ACCOUNT = ["profile.manage"];
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SPEAKING FOR THE BUSINESS TO SOMEBODY OUTSIDE IT — OWNER ✓, ADMIN ✓,
 * ACCOUNTANT ✗ (§17A.2 stage 1).
 *
 * ITS OWN LIST, and not folded into {@link CONVERSATION} above, even though the
 * two look alike from a distance. That one is the INTERNAL room: three people
 * who already have accounts, talking to each other, where the worst outcome of
 * a bad message is an awkward sentence nobody can delete. This one leaves the
 * building — a PDF in a customer's inbox, from the business's own domain, which
 * their AP system will file and act on. A grant that crosses that boundary is a
 * different decision about a different person, and the day someone is trusted
 * with one and not the other it should be a one-line change here.
 *
 * The CA is excluded for the ordinary §2 reason and not a special one; see the
 * capability's own note above for why it is not a fifth exception to her
 * read-only row.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const SEND_MAIL = ["mail.send"];
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * READING THE CORRESPONDENCE FILE — OWNER ✓, ADMIN ✓, ACCOUNTANT ✗ (§17A.1).
 *
 * ITS OWN LIST RATHER THAN A SECOND ENTRY IN {@link SEND_MAIL}, even though the
 * same two roles hold both today. Merging them would say that whoever may put a
 * quotation in a customer's inbox may also read every message the business has
 * exchanged with every customer — and the obvious next role, a clerk who mails
 * what the owner has approved, wants exactly one of those. The day that person
 * exists it should be a one-line change here, not the splitting of a list.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const READ_MAIL = ["mail.view"];
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SENDING THE OFFER OUT AND RECORDING THAT IT WENT — OWNER ✓, ADMIN ✓,
 * ACCOUNTANT ✗ (§17.1, behind `approval_links`).
 *
 * ITS OWN LIST RATHER THAN A LINE IN {@link SEND_MAIL}, even though the same
 * two roles hold both today. That one is the SMTP session; this one covers the
 * two doors that never touch it — a link pasted into WhatsApp and a QR printed
 * on paper — plus "I rang him and he has it". A business whose mailbox is not
 * configured still shares quotations, and the day somebody is trusted to hand a
 * sheet over but not to mail from the company address it should be one line
 * here.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const SHARE_DOCUMENTS = ["quotation.share"];
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WITHDRAWING A DOCUMENT YOU GOT WRONG — OWNER ✓, ADMIN ✓ (§2.1's amended row,
 * added 2026-08-13; the reasoning is in §2).
 *
 * THESE THREE WERE ADMIN-ONLY UNTIL THE 2026-08-13 AMENDMENT, AND THAT WAS THE
 * DEFECT. In practice it meant the man who uses this app every day could see a
 * bill he had got wrong and could do nothing about it; correcting it required
 * his son to be reachable. §2 records the two consequences, both worse than
 * letting him fix it:
 *
 *  1. HE WOULD WORK AROUND IT. Faced with a wrong bill he cannot void and a
 *     customer standing in front of him, the obvious move is to issue a second,
 *     correct bill and not mention the first — leaving TWO LIVE INVOICES for one
 *     job inside a gap-free sequence (§6). That is a far worse state for the
 *     books than one cancelled invoice with a reason attached.
 *  2. IT INVERTED THE ACTUAL AUTHORITY. He owns the business. The invoice is
 *     his, the GSTIN is his, the filing is in his name. A permission model in
 *     which the proprietor needs his son's account to void his own document
 *     describes the software's convenience, not the business.
 *
 * WHAT MAKES THIS SAFE is unchanged, and none of it is optional:
 *  - cancelling/voiding RETAINS THE NUMBER (§6, §4) — the series stays gap-free
 *    and an auditor sees a numbered, voided document rather than a hole;
 *  - a REASON IS MANDATORY on every one of these routes;
 *  - every attempt WRITES TO `audit_log` with the actor (§2.1 — denied attempts
 *    too, not only successful ones).
 * Nothing is destroyed and nothing is silent, which is why widening the map is
 * not the same as widening the blast radius.
 *
 * A SEPARATE LIST FROM {@link AUTHOR_DOCUMENTS} ON PURPOSE, even though the two
 * roles holding them are identical today. Authoring produces a document;
 * withdrawing one retracts a number that may already be in a customer's hands.
 * They are the row §2.1 is most likely to move again (an ACCOUNTANT or a future
 * SUPERVISOR would plainly get one set and not the other), and keeping them
 * apart means that move is a one-line data change here rather than a hunt
 * through a merged list.
 *
 * `item.adjust_stock` is deliberately NOT here — it stays ADMIN-only. It is not
 * a correction to a document the owner raised; it rewrites a quantity with no
 * document behind it at all, and §2.1's table leaves it on the ADMIN row.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const UNDO_OWN_MISTAKES = [
    "invoice.cancel",
    "challan.cancel",
    "quotation.void",
    /*
     * §17.3. It belongs in this list on both counts: it withdraws a numbered
     * document that is already in a customer's hands, the number is retained, and
     * a reason is mandatory. It also has a consequence the other three do not —
     * cancelling a credit note RETURNS the credited amount to the invoice's
     * outstanding balance, because every sum over `credit_notes` counts ISSUED
     * rows only. That is the correct behaviour (a cancelled credit note credits
     * nothing) and it is the reason the capability is not handed out more widely.
     */
    "creditnote.cancel",
];
/**
 * ADMIN-only capabilities (§2.1, rows 4 and 5): stock adjustment, and everything
 * that changes how the system computes or who may use it.
 *
 * The destructive DOCUMENT verbs used to be in this list and are not any more —
 * see {@link UNDO_OWN_MISTAKES} and the 2026-08-13 amendment in §2. What is left
 * here is the set the OWNER genuinely has no business in: the GSTIN, the PAN and
 * the bank details behind `settings.write`, the CA-verified tax master behind
 * `taxcode.write`, accounts, and the audit trail itself.
 */
const ADMIN_ONLY = [
    "item.adjust_stock",
    "settings.write",
    "taxcode.write",
    "user.manage",
    "audit.view",
];
/**
 * The §2.1 role → capability table, transcribed.
 *
 * Exported as readonly ARRAYS rather than `Set`s on purpose: arrays are
 * JSON-serialisable (the API ships the caller's capability list to the SPA in
 * the session payload), stable-ordered (so tests and diffs read cleanly), and
 * cheap to iterate for an ADMIN screen. Lookup speed is recovered by
 * {@link ROLE_CAPABILITY_SETS} below, an internal `Set` index built once at
 * module load, which is what {@link can} actually consults. A 27-entry linear
 * scan would be fine too; the index just makes `can()` O(1) for the middleware
 * that runs on every request.
 *
 * `Record<UserRole, ...>` means adding a role to the enum without granting it
 * capabilities here fails the typecheck — a new role cannot slip through
 * unmapped and silently inherit someone else's permissions.
 */
export const ROLE_CAPABILITIES = Object.freeze({
    /**
     * The father. Authors everything day to day, and **since the 2026-08-13
     * amendment (§2) may withdraw a document he got wrong** — cancel an invoice,
     * cancel a challan, void a quotation — each with a mandatory reason, each
     * keeping its number, each audited. He also records the money that comes in
     * (§17.5); he is the one who sees the bank statement.
     *
     * He still cannot adjust stock, or touch settings / tax codes / users / the
     * audit trail. And he cannot hard-delete anything, because NOTHING in this
     * system is hard-deleted (§2) — that is a property of the system, not a
     * permission anybody is missing.
     */
    OWNER: Object.freeze([
        ...VIEW_AND_EXPORT,
        ...AUTHOR_DOCUMENTS,
        ...COUNTER_MANAGEMENT,
        ...RECORD_RECEIPTS,
        ...RECORD_EWAY_BILLS,
        ...OWN_NOTIFICATIONS,
        ...CONVERSATION,
        ...PRIVATE_CONVERSATION,
        ...OWN_ACCOUNT,
        ...SEND_MAIL,
        ...READ_MAIL,
        ...SHARE_DOCUMENTS,
        ...UNDO_OWN_MISTAKES,
    ]),
    /** The son. Everything OWNER can, plus the ADMIN-only row. */
    ADMIN: Object.freeze([
        ...VIEW_AND_EXPORT,
        ...AUTHOR_DOCUMENTS,
        ...COUNTER_MANAGEMENT,
        ...RECORD_RECEIPTS,
        ...RECORD_EWAY_BILLS,
        ...OWN_NOTIFICATIONS,
        ...CONVERSATION,
        ...PRIVATE_CONVERSATION,
        ...OWN_ACCOUNT,
        ...SEND_MAIL,
        ...READ_MAIL,
        ...SHARE_DOCUMENTS,
        ...UNDO_OWN_MISTAKES,
        ...ADMIN_ONLY,
    ]),
    /**
     * The CA — LIVE SINCE 2026-08-17, behind the `accountant_role` flag (§18.5;
     * see {@link ROLE_FEATURE_FLAGS}). She signs in with email and password on the
     * ADMIN credential path, which is the stronger of the two the app has.
     *
     * READ-ONLY OVER DOCUMENT CONTENT, plus three narrow writes.
     *
     * §2 — "Cannot create, edit, cancel or delete anything." That still holds for
     * everything the invoice SAYS: the CA cannot author, amend or cancel a
     * document, and if they spot an error the path is a credit note (§17.3). An
     * outside party with write access to issued invoices breaks audit integrity.
     *
     * THE EXCEPTIONS, AND WHY EACH ONE IS NOT AN EDIT:
     *
     *  1. `invoice.record_irn` (§2's 2026-08-14 amendment) — attaching the IRN the
     *     Invoice Registration Portal returned.
     *  2. `eway.record` (2026-08-17) — attaching the 12-digit e-way bill number the
     *     NIC portal issued. SHE IS THE ONE WHO LOGS INTO THAT PORTAL; the owner
     *     does not. Same shape as the IRN: four reserved columns, no figure on the
     *     document derived from them.
     *  3. `notification.mark_read` — clearing her OWN inbox row. It touches no
     *     document at all, and without it the person this notification hub was
     *     built for could never clear the thing it tells her.
     *  4. `message.send` — writing a sentence in the shared conversation. The
     *     newest of the four and the only one that is not "file something the
     *     government issued", so it is worth being explicit: a message changes no
     *     line, no figure, no party and no number on any document. The CA is the
     *     one who spots that an HSN is wrong or that a credit note is needed
     *     before filing, and a conversation she can read but not answer sends
     *     that finding to a phone call, where it is not written down at all.
     *
     * `permissions.test.ts` scans this list for mutating capabilities and names
     * those four explicitly, so a FIFTH write added here fails CI rather than
     * reaching production. The scan is deliberately a literal list rather than a
     * pattern like "anything `record_*` is fine" — a pattern would admit the next
     * write somebody invents without anyone arguing for it in writing.
     *
     * REACHABLE SINCE 2026-08-17, when the owner decided it: "CA WILL have their
     * login and their view." She signs in on the ADMIN credential path (email +
     * password, the stronger of the two) and holds exactly this list — no
     * capability changed on the day her session became possible, which was the
     * whole point of writing the row before the login existed. Whether the role
     * may hold a session at all is {@link ROLE_FEATURE_FLAGS} below, and it is
     * still OFF until the owner turns it on.
     *
     * WHAT SHE STILL MUST NOT SEE, and it is not expressible here: `settings.view`
     * is in {@link VIEW_AND_EXPORT}, so she can read the company profile — which
     * carries the PAN and the bank account. A capability cannot say "these five
     * columns", so `GET /company` PROJECTS them out for any caller without
     * `settings.write`. See routes/company.ts.
     */
    ACCOUNTANT: Object.freeze([
        ...VIEW_AND_EXPORT,
        "invoice.record_irn",
        ...RECORD_EWAY_BILLS,
        ...OWN_NOTIFICATIONS,
        ...CONVERSATION,
        ...PRIVATE_CONVERSATION,
        /*
         * HER OWN ACCOUNT (2026-08-18). She holds it for the reason everybody does,
         * and for one more that applies to her alone: she is the EXTERNAL party, so
         * hers is the account whose password most needs rotating without a phone
         * call to the son — and the alternative to letting her change it is that
         * somebody else knows it. It touches no document, no line, no figure, no
         * party and no number, and it cannot reach her role, her active state or her
         * sign-in door; see the capability's own note.
         */
        ...OWN_ACCOUNT,
    ]),
});
/** Internal O(1) index over {@link ROLE_CAPABILITIES}. Built once, never exported. */
const ROLE_CAPABILITY_SETS = new Map(USER_ROLES.map((role) => [role, new Set(ROLE_CAPABILITIES[role])]));
/**
 * The same index, but holding the LIST for {@link capabilitiesForRole}.
 *
 * A `Map` rather than an index straight into {@link ROLE_CAPABILITIES}, for the
 * reason `can()` already uses one: `ROLE_CAPABILITIES` is a plain object, so
 * `ROLE_CAPABILITIES[role]` walks `Object.prototype` when `role` is a string
 * like `"toString"`, `"constructor"`, `"valueOf"`, `"hasOwnProperty"` or
 * `"__proto__"`. Those lookups return a FUNCTION (or `Object.prototype` itself)
 * rather than `undefined`, so a `?? []` deny-by-default fallback never fires and
 * the caller is handed a non-array as if it were a capability list. The role
 * arrives from a decoded token, so it is a string chosen by someone else.
 * `Map.get` only ever sees keys that were explicitly put in.
 *
 * Built once at module load, so the lookup stays O(1) for the session payload
 * and the middleware.
 */
const ROLE_CAPABILITY_LISTS = new Map(USER_ROLES.map((role) => [role, ROLE_CAPABILITIES[role]]));
/** Shared frozen empty list, so the deny-by-default answer is never mutable. */
const NO_CAPABILITIES = Object.freeze([]);
/* -------------------------------------------------------------------------- */
/* 3. The resolver (§2.1) — deny by default                                    */
/* -------------------------------------------------------------------------- */
/**
 * Does `role` hold `capability`?
 *
 * DENY BY DEFAULT (§2.1). This function never throws and never falls back to
 * `true`:
 *  - an unknown / absent / malformed role → `false`
 *  - a capability not in the union, or not in that role's list → `false`
 *
 * Both parameters are typed to the unions so mistakes are caught at compile
 * time, but the body treats them as untrusted strings, because in practice the
 * role arrives from a decoded token and the capability may arrive from a route
 * table. Deny-by-default has to hold at runtime, not just in the type system.
 *
 * This is the ONLY authorisation predicate. `requireCap` on the server and the
 * `useCan` hook on the client both call it, which is what guarantees the UI and
 * the API can never disagree. Client-side checks remain UX only — hiding a
 * button is not security; the server check is the one that counts.
 *
 * Denied attempts must still be audited (§2.1) — that is the caller's job, not
 * this function's; `can()` stays pure so it is safe to call in a render path.
 */
export function can(role, capability) {
    if (typeof role !== "string" || typeof capability !== "string")
        return false;
    const granted = ROLE_CAPABILITY_SETS.get(role);
    if (granted === undefined)
        return false;
    return granted.has(capability);
}
/* -------------------------------------------------------------------------- */
/* 4. The one DIRECTION rule (§6.1) — holding a capability is not enough        */
/* -------------------------------------------------------------------------- */
/**
 * Which way a document counter is being moved.
 *
 * `"forward"` covers "raise the sequence" and "move `current_fy` on"; it leaves
 * a GAP. `"backward"` covers lowering either, and it re-issues numbers that are
 * already in a customer's hands.
 */
export const COUNTER_DIRECTIONS = ["forward", "backward"];
/**
 * Roles that may move a counter BACKWARD (§6.1).
 *
 * WHY THIS ISN'T A `role === "ADMIN"` CHECK IN A HANDLER. §2.1's rule is that
 * the role → permission decision lives in ONE file — this one — so that adding
 * ACCOUNTANT (or a future SUPERVISOR) is a data change here rather than a hunt
 * through routes and components. §6.1 states the counter rule per role and only
 * per role ("OWNER may only move a counter FORWARD ... ADMIN may move it either
 * way"), so the role is genuinely the input; what §2.1 forbids is *spreading*
 * that decision, not knowing it. Handlers call {@link canSetCounter} and the
 * SPA's confirmation dialog calls the same function, so — exactly as with
 * {@link can} — the UI and the API cannot disagree about who may rewind the
 * series.
 *
 * It is NOT a separate capability because §2.1 lists exactly one
 * (`invoice.set_counter`), and inventing `invoice.set_counter_backward` would
 * quietly extend the contract the client and server both compile against.
 */
const COUNTER_BACKWARD_ROLES = new Set(["ADMIN"]);
/**
 * May `role` move a document counter in this direction? (§6.1)
 *
 * Deny by default, like {@link can}: an unknown role, an unknown direction or a
 * role without `invoice.set_counter` all answer `false`.
 *
 * The asymmetry is the whole point, and it is not squeamishness about a big
 * number:
 *
 *  - FORWARD leaves an unused number. Untidy, explainable in one sentence to the
 *    CA, and legal.
 *  - BACKWARD hands the same invoice number to a second document while the first
 *    is already in the customer's accounts payable. That is the single failure
 *    §6 exists to prevent, and no explanation repairs it.
 *
 * The ADMIN keeps the backward move because the migration from the manual
 * Word/Excel process (§6, migration note) genuinely needs it.
 */
export function canSetCounter(role, direction) {
    if (typeof role !== "string" || typeof direction !== "string")
        return false;
    if (!can(role, "invoice.set_counter"))
        return false;
    if (direction === "forward")
        return true;
    if (direction === "backward")
        return COUNTER_BACKWARD_ROLES.has(role);
    return false;
}
/**
 * Every capability held by `role`, or an EMPTY array for an unknown role —
 * deny by default again, never a throw.
 *
 * Used to ship the caller's capabilities to the client in the session payload
 * and to render the ADMIN user screen. The returned array is frozen; treat it
 * as read-only.
 *
 * Resolves through {@link ROLE_CAPABILITY_LISTS} rather than indexing the plain
 * `ROLE_CAPABILITIES` object: see that map's comment — `role` is an untrusted
 * string, and `"toString"` must return an empty list, not `Object.prototype`'s
 * method.
 */
export function capabilitiesForRole(role) {
    if (typeof role !== "string")
        return NO_CAPABILITIES;
    return ROLE_CAPABILITY_LISTS.get(role) ?? NO_CAPABILITIES;
}
/* -------------------------------------------------------------------------- */
/* 5. Which roles may hold a session at all (§2, §18.5)                        */
/* -------------------------------------------------------------------------- */
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ROLES WHOSE AVAILABILITY IS DECIDED BY A FEATURE FLAG (§18.5).
 *
 * A role that is NOT a key here is always available. A role that IS a key holds
 * a session only while its flag is on — checked at login, at refresh, and on
 * every authenticated request, because the flag can be switched off mid-session
 * and the next request is when that must take effect.
 *
 * THIS REPLACED `IMPLEMENTED_USER_ROLES` on 2026-08-17. That was a second,
 * shorter role list in enums.ts, and the CA was excluded from it. Deleting it in
 * favour of this map is not cosmetic: a list of "roles we have built" answers a
 * question that stops being interesting the moment every role is built, while
 * "which flag governs this role" is answerable for a role nobody has invented
 * yet, and it lives next to the capability map rather than next to the enum.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * §18.3 SAYS FLAGS MAY NEVER GATE "AUTHENTICATION OR THE PERMISSION CHECKS
 * THEMSELVES", AND THIS IS THE ONE PLACE THAT COMES NEAR THE LINE. It is worth
 * being explicit about which side of it this map sits on, because the next
 * person to read §18.3 will ask.
 *
 * What §18.3 forbids is a flag that can make authentication WEAKER or a
 * capability check LOOSER — a switch that skips a credential, that lets a role
 * through a gate it does not hold, or that turns a 403 into a 200. This map can
 * do none of those things, in either state, and the direction is what makes it
 * safe: it can only ever DENY. Flag off, a role that would otherwise be allowed
 * is refused; flag on, the account still has to present the right password,
 * still passes the same lockout and the same rate limiter, and still holds
 * exactly the capabilities {@link ROLE_CAPABILITIES} grants it. There is no
 * value of any flag that authenticates somebody who could not otherwise
 * authenticate, and none that grants a capability.
 *
 * The failure mode §18.1 worries about points the same way: a flag lookup that
 * fails resolves to the §18.5 default, which for `accountant_role` is OFF. A
 * database outage therefore locks the CA out rather than letting her in.
 *
 * WHAT MUST NOT BE ADDED HERE: a flag for OWNER or ADMIN. Flagging the roles
 * that run the business means a flag row can stop the owner billing, which is
 * precisely the compliance defect §18.3 exists to prevent. The map is for a role
 * being rolled out, and it should empty itself again — when the CA login is
 * stable, delete the flag AND this entry in the same commit (§18.4).
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const ROLE_FEATURE_FLAGS = Object.freeze({
    ACCOUNTANT: "accountant_role",
});
/** Internal `Map` index, for the `Object.prototype` reason `can()` uses one. */
const ROLE_FLAG_INDEX = new Map(USER_ROLES.flatMap((role) => {
    const key = ROLE_FEATURE_FLAGS[role];
    return key === undefined ? [] : [[role, key]];
}));
/**
 * The feature flag governing this role, or `null` when the role needs no flag.
 *
 * An UNKNOWN role answers `null` too, and that is deliberately not a hole: the
 * caller's next question is "is this a role at all", answered by the enum, and a
 * role outside {@link ROLE_CAPABILITIES} holds no capabilities anyway
 * ({@link can} denies everything). Returning a flag key for a string nobody
 * defined would be inventing a gate rather than closing one.
 */
export function roleFeatureFlag(role) {
    if (typeof role !== "string")
        return null;
    return ROLE_FLAG_INDEX.get(role) ?? null;
}
/** Is this string one of the three roles in {@link USER_ROLES}? Deny by default. */
export function isUserRole(value) {
    return typeof value === "string" && USER_ROLES.includes(value);
}
//# sourceMappingURL=permissions.js.map